import React, { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import CloseIcon from '@mui/icons-material/Close';
import CrmButton from './CrmButton';
import './crm-ui.scss';

function CrmDrawer({
  open,
  onClose,
  title,
  subtitle,
  tabs,
  children,
  footer,
  size = 'md',
  closeOnBackdrop = true,
  closeOnEscape = true,
  className = '',
}) {
  const titleId = useId();
  const drawerRef = useRef(null);
  const onCloseRef = useRef(onClose);

  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);

  useEffect(() => {
    if (!open) return undefined;

    const previousActive = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const focusTimer = requestAnimationFrame(() => drawerRef.current?.focus());
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && closeOnEscape) {
        event.preventDefault();
        onCloseRef.current?.();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      cancelAnimationFrame(focusTimer);
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = previousOverflow;
      previousActive?.focus?.();
    };
  }, [closeOnEscape, open]);

  if (!open) return null;

  return createPortal(
    <div
      className="crm-drawer-backdrop"
      onMouseDown={(event) => {
        if (closeOnBackdrop && event.target === event.currentTarget) onClose?.();
      }}
    >
      <aside
        ref={drawerRef}
        className={`crm-drawer crm-drawer--${size} ${className}`.trim()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        tabIndex={-1}
      >
        <header className="crm-drawer__header">
          <div>
            {title ? <h2 id={titleId} className="crm-drawer__title">{title}</h2> : null}
            {subtitle ? <p className="crm-drawer__subtitle">{subtitle}</p> : null}
          </div>
          {onClose ? (
            <CrmButton
              iconOnly
              variant="tertiary"
              size="small"
              onClick={onClose}
              aria-label="Close drawer"
            >
              <CloseIcon fontSize="small" />
            </CrmButton>
          ) : null}
        </header>
        {tabs ? <div className="crm-drawer__tabs">{tabs}</div> : null}
        <div className="crm-drawer__body">{children}</div>
        {footer ? <footer className="crm-drawer__footer">{footer}</footer> : null}
      </aside>
    </div>,
    document.body
  );
}

export default CrmDrawer;
